import React, { useState, useEffect, useCallback } from "react";
import { Badge, Spinner } from "react-bootstrap";
import { useLocation, Link } from "react-router-dom";
import axiosInstance from "../../../services/AxiosInstance";
import swal from "sweetalert";
import PageTitle from "../../layouts/PageTitle";
import { format } from "date-fns";

const useQuery = () => new URLSearchParams(useLocation().search);

const STATUS_BADGE = {
  pending:    { bg: "warning",   text: "dark",  label: "Pending"    },
  processing: { bg: "info",      text: "white", label: "Sending…"   },
  posted:     { bg: "success",   text: "white", label: "Posted ✓"   },
  failed:     { bg: "danger",    text: "white", label: "Failed"      },
  cancelled:  { bg: "secondary", text: "white", label: "Cancelled"   },
};

const ChannelPosts = () => {
  const query      = useQuery();
  const channelId  = query.get("channelId")   || "";
  const channelName = query.get("channelName") || "All Channels";

  const [posts, setPosts]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage]           = useState(1);
  const [total, setTotal]         = useState(0);
  const limit = 20;

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (channelId)    params.set("channelId", channelId);
      if (statusFilter) params.set("status", statusFilter);

      const { data } = await axiosInstance.get(`channels/posts?${params.toString()}`);
      if (data.status) {
        setPosts(data.data || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error("ChannelPosts load error:", err);
    }
    setLoading(false);
  }, [channelId, statusFilter, page]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const cancelPost = async (post) => {
    const confirm = await swal({
      title: "Cancel this post?",
      text: "The post will not be sent.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!confirm) return;
    try {
      const { data } = await axiosInstance.delete(`channels/posts/${post._id}`);
      if (data.status) {
        swal("Cancelled", "Post has been cancelled.", "success");
        loadPosts();
      } else {
        swal("Error", data.message, "error");
      }
    } catch (err) {
      swal("Error", err?.response?.data?.message || err.message, "error");
    }
  };

  const retryPost = async (post) => {
    try {
      const { data } = await axiosInstance.post(`channels/posts/${post._id}/retry`);
      if (data.status) {
        swal("Queued", "Post will be retried in 2 minutes.", "success");
        loadPosts();
      } else {
        swal("Error", data.message, "error");
      }
    } catch (err) {
      swal("Error", err?.response?.data?.message || err.message, "error");
    }
  };

  const formatDate = (d) => {
    try { return format(new Date(d), "dd MMM yyyy, hh:mm a"); } catch { return d; }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <>
      <PageTitle
        activeMenu={channelName ? `Posts — ${decodeURIComponent(channelName)}` : "All Posts"}
        motherMenu="Channel Scheduler"
      />

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h4 className="card-title mb-0">
                {channelName ? `📢 ${decodeURIComponent(channelName)}` : "All Channel Posts"}
              </h4>
              <div className="d-flex gap-2 align-items-center">
                <select
                  className="form-control form-control-sm"
                  style={{ width: 160 }}
                  value={statusFilter}
                  onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="posted">Posted</option>
                  <option value="failed">Failed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <Link
                  to={channelId
                    ? `/channel-schedule?channelId=${channelId}&channelName=${channelName}`
                    : "/channel-manager"
                  }
                  className="btn btn-primary btn-sm"
                >
                  + Schedule Post
                </Link>
                <Link to="/channel-manager" className="btn btn-outline-secondary btn-sm">
                  ← Channels
                </Link>
              </div>
            </div>

            <div className="card-body">
              {loading ? (
                <div className="text-center py-4"><Spinner animation="border" variant="primary" /></div>
              ) : posts.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <p>No posts found.</p>
                  <Link
                    to={channelId
                      ? `/channel-schedule?channelId=${channelId}&channelName=${channelName}`
                      : "/channel-manager"
                    }
                    className="btn btn-primary btn-sm"
                  >
                    Schedule Your First Post
                  </Link>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>
                          <th>Channel</th>
                          <th>Post Text</th>
                          <th>Media</th>
                          <th>Scheduled At</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {posts.map(post => {
                          const badge = STATUS_BADGE[post.status] || STATUS_BADGE.pending;
                          return (
                            <tr key={post._id}>
                              <td>
                                <span className="fw-bold small">
                                  📢 {post.channelId?.name || "—"}
                                </span>
                              </td>
                              <td style={{ maxWidth: 200 }}>
                                <span className="small" style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {post.text || <em className="text-muted">No text</em>}
                                </span>
                                {post.isRecurring && (
                                  <Badge bg="info" text="white" className="mt-1" style={{ fontSize: 10 }}>
                                    🔁 {post.recurringType}
                                  </Badge>
                                )}
                              </td>
                              <td>
                                {post.mediaFiles?.length > 0 ? (
                                  <Badge bg="secondary" text="white" style={{ fontSize: 10 }}>
                                    {post.mediaFiles[0].type}
                                  </Badge>
                                ) : post.pollData?.question ? (
                                  <Badge bg="info" text="white" style={{ fontSize: 10 }}>poll</Badge>
                                ) : (
                                  <span className="text-muted small">—</span>
                                )}
                              </td>
                              <td>
                                <span className="small">{formatDate(post.scheduledAt)}</span>
                                {post.postedAt && (
                                  <div className="text-muted" style={{ fontSize: 11 }}>
                                    Sent: {formatDate(post.postedAt)}
                                  </div>
                                )}
                              </td>
                              <td>
                                <Badge bg={badge.bg} text={badge.text}>
                                  {badge.label}
                                </Badge>
                                {post.retryCount > 0 && (
                                  <div className="text-muted" style={{ fontSize: 11 }}>
                                    Retry #{post.retryCount}
                                  </div>
                                )}
                                {post.errorMessage && (
                                  <div className="text-danger" style={{ fontSize: 11, maxWidth: 150 }}
                                    title={post.errorMessage}>
                                    {post.errorMessage.length > 40
                                      ? post.errorMessage.slice(0, 40) + "…"
                                      : post.errorMessage}
                                  </div>
                                )}
                              </td>
                              <td>
                                <div className="d-flex gap-1 flex-wrap">
                                  {["pending"].includes(post.status) && (
                                    <button
                                      className="btn btn-outline-danger btn-xs"
                                      onClick={() => cancelPost(post)}
                                      title="Cancel post"
                                    >
                                      Cancel
                                    </button>
                                  )}
                                  {post.status === "failed" && (
                                    <button
                                      className="btn btn-outline-primary btn-xs"
                                      onClick={() => retryPost(post)}
                                      title="Retry post"
                                    >
                                      Retry
                                    </button>
                                  )}
                                  {post.screenshotUrl && (
                                    <a
                                      href={post.screenshotUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="btn btn-outline-secondary btn-xs"
                                      title="View screenshot"
                                    >
                                      📷
                                    </a>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <span className="text-muted small">{total} total posts</span>
                      <div className="d-flex gap-1">
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          disabled={page <= 1}
                          onClick={() => setPage(p => p - 1)}
                        >
                          ← Prev
                        </button>
                        <span className="btn btn-sm disabled">{page} / {totalPages}</span>
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          disabled={page >= totalPages}
                          onClick={() => setPage(p => p + 1)}
                        >
                          Next →
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChannelPosts;
